'use client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmailViewerProps } from './email-viewer-types';
import { EmailHeader } from './email-header';
import { EmailBody } from './email-body';
import { EmailAttachment } from './email-attachment';
import { EmailExportButton } from './EmailExportButton';
import ExtractedDataTab from './ExtractedDataTab';
import OCRResultsTab from './OCRResultsTab';
import { Separator } from '@/components/ui/separator';
import { T, Var } from 'gt-react';
// Demo (delete me) — hide the admin "export email" debug button during any demo.
import { getActiveDemo } from '@/demo/core/demo-mode';

const hideDataTabs = true;

export function EmailViewerV2({
  emailData,
  fetchExtractedPurchaseOrder,
  fetchOCR,
  defaultTab = 'email',
  onRefreshData,
  splitDocuments,
}: EmailViewerProps) {
  if (!emailData) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
        <T context="Empty email viewer placeholder">No email to display</T>
      </div>
    );
  }
  const attachmentCount =
    (emailData.attachments?.length ?? 0) +
    (emailData.documentAssociations?.length ?? 0);
  const hasAttachments = attachmentCount > 0;

  const showOCRTab = !!fetchOCR && !!emailData.inboundEmailId;
  const showExtractedDataTab =
    !!fetchExtractedPurchaseOrder && !!emailData.inboundEmailId;

  return (
    <div className="h-full overflow-y-scroll">
      <Tabs defaultValue={defaultTab} className="h-full">
        <div className="p-1 sticky top-0 bg-background flex items-center gap-2">
          <TabsList className="flex-1">
            <TabsTrigger value="email">
              <T context="Email viewer tab">Email</T>
            </TabsTrigger>
            <TabsTrigger value="attachments">
              <T context="Email viewer tab with attachment count">
                Attachments (<Var>{attachmentCount}</Var>)
              </T>
            </TabsTrigger>
            {showOCRTab && !hideDataTabs && (
              <TabsTrigger value="ocr">
                <T context="Email viewer OCR tab">OCR</T>
              </TabsTrigger>
            )}
            {showExtractedDataTab && !hideDataTabs && (
              <TabsTrigger value="extracted-data">
                <T context="Email viewer extracted data tab">Extracted Data</T>
              </TabsTrigger>
            )}
          </TabsList>
          {/* Demo (delete me) — suppress the debug export button in demo mode */}
          {!getActiveDemo() && (
            <EmailExportButton inboundEmailId={emailData.inboundEmailId} />
          )}
        </div>

        <Separator />

        <TabsContent value="email">
          <div className="flex flex-col gap-1">
            <EmailHeader emailData={emailData} />
            <EmailBody emailData={emailData} />
          </div>
        </TabsContent>

        <TabsContent value="attachments">
          <EmailAttachment
            emailData={emailData}
            onRefreshData={onRefreshData}
            splitDocuments={splitDocuments}
          />
        </TabsContent>

        {showOCRTab && !hideDataTabs && (
          <TabsContent value="ocr">
            <OCRResultsTab
              inboundEmailId={emailData.inboundEmailId}
              attachments={emailData.attachments}
            />
          </TabsContent>
        )}

        {showExtractedDataTab && !hideDataTabs && (
          <TabsContent value="extracted-data">
            <ExtractedDataTab
              inboundEmailId={emailData.inboundEmailId}
              hasAttachments={hasAttachments}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
